/*
 * castle-rat-header.js — fixed-header behavior for the Castle Rat header.
 * One custom element, no dependencies. It (1) keeps the page content
 * offset in sync with the fixed bar's measured height (--cr-header-height),
 * and (2) drives the mobile drawer (the CSS owns visual state via the
 * `.is-open` class; this flips it and keeps ARIA + scroll-lock in sync).
 */
if (!customElements.get('castle-rat-header')) {
  customElements.define(
    'castle-rat-header',
    class CastleRatHeader extends HTMLElement {
      constructor() {
        super();
        this.bar = this.querySelector('.cr-header__bar');
        this.toggle = this.querySelector('.cr-header__toggle');
        this.nav = this.querySelector('#cr-nav-rail');
      }

      connectedCallback() {
        // Always keep the content offset matched to the fixed bar height,
        // even if there's no nav rail/toggle.
        this.syncHeaderHeight = this.syncHeaderHeight.bind(this);
        if (this.bar) {
          this.syncHeaderHeight();
          window.addEventListener('resize', this.syncHeaderHeight);
          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(this.syncHeaderHeight);
            this.resizeObserver.observe(this.bar);
          }
        }

        // Auto-hide on scroll (mobile only — see media query in CSS).
        // Hide when scrolling down past the bar height; show on scroll up.
        this.lastScrollY = window.scrollY;
        this.scrollTicking = false;
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });

        // Nav-dependent handlers bind whenever there's a nav (the desktop
        // collapsible rail needs them even when the mobile toggle is hidden,
        // e.g. the homepage). The mobile toggle binds separately.
        if (!this.nav) return;
        this.collapsible = this.nav.classList.contains('cr-nav--collapsible');
        this.onKeydown = this.onKeydown.bind(this);
        this.onNavClick = this.onNavClick.bind(this);
        this.onOutsideClick = this.onOutsideClick.bind(this);

        this.addEventListener('keydown', this.onKeydown);
        this.nav.addEventListener('click', this.onNavClick);
        // Click outside the drawer (mobile) / pinned rail (desktop) closes it.
        document.addEventListener('click', this.onOutsideClick);

        if (this.toggle) {
          this.onToggle = this.onToggle.bind(this);
          this.toggle.addEventListener('click', this.onToggle);
        }

        // Collapsible (slide) rail only: when the post slides open/closed, swing
        // the hanging signs sideways on their chains (see .is-swinging in CSS).
        if (this.collapsible) {
          this.railOpen = false;
          this.evalRailOpen = this.evalRailOpen.bind(this);
          this.scheduleRailEval = this.scheduleRailEval.bind(this);
          this.onSwingEnd = this.onSwingEnd.bind(this);
          // Hover no longer opens the rail, so only focus + pin change state.
          this.swingEvents = ['focusin', 'focusout'];
          this.swingEvents.forEach((ev) =>
            this.nav.addEventListener(ev, this.scheduleRailEval)
          );
          this.nav.addEventListener('animationend', this.onSwingEnd);
        }
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.syncHeaderHeight);
        window.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
        this.toggle?.removeEventListener('click', this.onToggle);
        this.removeEventListener('keydown', this.onKeydown);
        this.nav?.removeEventListener('click', this.onNavClick);
        document.removeEventListener('click', this.onOutsideClick);
        if (this.collapsible && this.nav) {
          this.swingEvents.forEach((ev) =>
            this.nav.removeEventListener(ev, this.scheduleRailEval)
          );
          this.nav.removeEventListener('animationend', this.onSwingEnd);
        }
      }

      onScroll() {
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.evaluateScrollDirection();
          this.scrollTicking = false;
        });
      }

      evaluateScrollDirection() {
        // Don't hide while the mobile drawer is open or while the entrance
        // overlay is active — user is interacting with the nav itself.
        if (this.isOpen) return;
        const y = window.scrollY;
        const barH = this.bar ? this.bar.offsetHeight : 64;

        // Ignore iOS rubber-band overscroll (past the top or bottom). There the
        // scroll position springs back, producing phantom direction deltas that
        // would flash the bar in/out "on their own". Don't react, and don't
        // record the bouncy position as our reference.
        const maxY = document.documentElement.scrollHeight - window.innerHeight;
        if (y < 0 || y > maxY) return;

        // is-scrolled: simply "past the top". Used on desktop to hide the social
        // icons once the page scrolls; stable (not direction-based) so it can't
        // flicker.
        this.classList.toggle('is-scrolled', y > barH);

        // Near the top, always show the bar.
        if (y <= barH) {
          this.classList.remove('is-scrolled-down');
          this.lastScrollY = y;
          return;
        }

        // Direction-based auto-hide with a dead zone: require a sustained move
        // (THRESHOLD px) in one direction before toggling. Below that, leave the
        // state AND the reference position untouched so tiny iOS scroll jitter /
        // address-bar resize wobble accumulates rather than flipping the bar.
        const THRESHOLD = 10;
        const delta = y - this.lastScrollY;
        if (delta > THRESHOLD) {
          this.classList.add('is-scrolled-down'); // scrolling down -> hide
          this.lastScrollY = y;
        } else if (delta < -THRESHOLD) {
          this.classList.remove('is-scrolled-down'); // scrolling up -> show
          this.lastScrollY = y;
        }
      }

      syncHeaderHeight() {
        document.documentElement.style.setProperty(
          '--cr-header-height',
          this.bar.offsetHeight + 'px'
        );
      }

      get isOpen() {
        return this.classList.contains('is-open');
      }

      setOpen(open) {
        this.classList.toggle('is-open', open);
        // Always show the header bar while the drawer is open.
        if (open) this.classList.remove('is-scrolled-down');
        if (this.toggle) this.toggle.setAttribute('aria-expanded', String(open));
        // Lock body scroll only while the drawer is open on mobile.
        document.body.style.overflow = open ? 'hidden' : '';
        if (!open && this.toggle) this.toggle.focus();
      }

      onToggle() {
        this.setOpen(!this.isOpen);
      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;
        if (this.isOpen) this.setOpen(false);
        else if (this.collapsible) {
          this.nav.classList.remove('is-pinned');
          this.scheduleRailEval();
        }
      }

      // --- Slide-rail sign swing -------------------------------------------
      // Defer to the next frame so :hover / :focus-within / .is-pinned have
      // settled before we read the open state.
      scheduleRailEval() {
        requestAnimationFrame(this.evalRailOpen);
      }

      evalRailOpen() {
        if (!this.collapsible) return;
        const open =
          this.nav.matches(':focus-within') ||
          this.nav.classList.contains('is-pinned');
        if (open === this.railOpen) return;
        this.railOpen = open;
        this.swingSigns(open);
      }

      swingSigns(open) {
        // Desktop slide rail only, and never under reduced-motion.
        if (window.innerWidth < 990) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        // Opening slides the post right, so the signs lag left (-1); closing is
        // the reverse (+1). Restart the animation by removing the class, forcing
        // a reflow, then re-adding it.
        this.nav.style.setProperty('--cr-swing-dir', open ? '-1' : '1');
        this.nav.classList.remove('is-swinging');
        void this.nav.offsetWidth;
        this.nav.classList.add('is-swinging');
      }

      onSwingEnd(event) {
        if (event.animationName === 'cr-signs-drag') {
          this.nav.classList.remove('is-swinging');
        }
      }

      onNavClick(event) {
        // Mobile drawer: close after choosing a destination.
        if (this.isOpen) {
          if (event.target.closest('a')) this.setOpen(false);
          return;
        }
        // Desktop collapsible: the rail opens on CLICK (not hover). The first
        // click on the peeking rail slides it open (pins it) instead of
        // following a link; once it's pinned open, clicks navigate normally.
        if (
          this.collapsible &&
          !this.nav.classList.contains('is-pinned') &&
          window.innerWidth >= 990
        ) {
          event.preventDefault();
          this.nav.classList.add('is-pinned');
          this.scheduleRailEval();
        }
      }

      onOutsideClick(event) {
        // Mobile drawer: click outside closes it.
        if (this.isOpen) {
          if (this.nav.contains(event.target) || (this.toggle && this.toggle.contains(event.target))) return;
          this.setOpen(false);
          return;
        }
        // Desktop collapsible: click outside the pinned rail collapses it again.
        if (this.collapsible && this.nav.classList.contains('is-pinned') && !this.nav.contains(event.target)) {
          this.nav.classList.remove('is-pinned');
          this.scheduleRailEval();
        }
      }
    }
  );
}
